<script setup lang="ts">
    import { formatInstallelsetid } from "@/tabs/Operation/calendar-utils.js";
    import Status from "@/tabs/Operation/Status.vue";
    import { anmalanStatusIcons } from "@/tabs/Operation/utilsTs";
    import PreliminarbokningIcon from "@/assets/preliminarbokning.svg";
    import { Resurs } from "@/components/forms/reusables/resurserHelper";
    import { useStore } from "@/store";
    import { ConfigModuleName, ConfigType, OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";

    interface Props {
        tipBorder?: string;
        dateBooked?: string;
        salstid?: string;
        patient?: string;
        huvudoperationkod?: string;
        lateralitet?: string;
        optidTidpunktStart?: string;
        optid?: number | null;
        operatorer?: string;
        operationsal?: string;
        resurskrockar?: Resurs[];
        installelsedatum?: string;
        installelsetid?: string;
        avtal?: string;
        status?: string;
        anestesi?: string;
        preliminarbokning?: boolean;
        nivaer?: string;
        implantat?: string;
        operationsinformation?: string;
    }

    defineProps<Props>();

    // Status-komponenten slår upp ikon via strängnyckeln – "true" matchar boolean-propen preliminarbokning
    const preliminarbokningIcon: Record<string, string> = {
        true: PreliminarbokningIcon,
    };

    const store = useStore();

    function isVisible(name: string, type: ConfigType = ConfigType.Field) {
        return store.getters["config/shouldBeVisibleByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.KalenderDetaljinfo,
            type,
            name,
        });
    }
</script>

<template>
    <div class="calendar-tool-tip">
        <div class="tool-tip-header-border-container">
            <div class="stripes-overlay" v-if="preliminarbokning"></div>
            <div class="tool-tip-header-border" :style="{ borderTop: `6px solid ${tipBorder}` }" />
        </div>
        <div class="tool-tip-header">
            <div>
                <h4>{{ dateBooked }}</h4>
                <p v-if="salstid && isVisible('salstid')">Salstid {{ salstid }}</p>
            </div>
            <div v-if="isVisible('status')" class="tool-tip-header-right">
                <Status
                    v-if="preliminarbokning"
                    :status="preliminarbokning"
                    text="Preliminärbokad"
                    :icons="preliminarbokningIcon"
                />
                <Status v-else-if="status" :status="status" :text="status" :icons="anmalanStatusIcons" />
            </div>
        </div>
        <div class="tool-tip-op-info">
            <div class="tool-tip-info-wrapper">
                <section v-if="isVisible('huvudinfo', ConfigType.Section)" class="tool-tip-info-section">
                    <div v-if="optid && isVisible('optid')">
                        <img src="@/assets/icons/operationstid.svg" alt="Image of person and + icon beside it" />
                        <span>Operationstid {{ optid }} minuter</span>
                    </div>
                    <div v-if="patient && isVisible('patient')">
                        <img src="@/assets/actionIcons/add_patient.svg" alt="Image of person and + icon beside it" />
                        <span>{{ patient }}</span>
                    </div>
                    <div v-if="huvudoperationkod && isVisible('huvudoperationkod')">
                        <img src="@/assets/actionIcons/opstart_i_opjournal.svg" alt="Image of person lying in a bed" />
                        <span>{{ huvudoperationkod }} </span>
                    </div>

                    <div v-if="operatorer && isVisible('operatorer')">
                        <img src="@/assets/actionIcons/add_operator.svg" alt="Image of an profile" />
                        <span>{{ operatorer }}</span>
                    </div>
                    <div v-if="operationsal && isVisible('operationsal')">
                        <img src="@/assets/actionIcons/location_pin.svg" alt="Image of location pin" />
                        <span>{{ operationsal }}</span>
                    </div>
                </section>

                <section v-if="isVisible('detaljer', ConfigType.Section)" class="tool-tip-info-section">
                    <div v-if="lateralitet && isVisible('lateralitet')">
                        <img src="@/assets/icons/opsida.svg" alt="Image of person and + icon beside it" />
                        <span>{{ lateralitet }}</span>
                    </div>
                    <div v-if="nivaer && isVisible('nivaer')">
                        <img src="@/assets/icons/nivaer.svg" alt="Nivåer" />
                        <span>{{ nivaer }}</span>
                    </div>
                    <div v-if="anestesi && isVisible('anestesi')">
                        <img src="@/assets/anestesi.svg" alt="Anestesi" />
                        <span>{{ anestesi }}</span>
                    </div>
                    <div v-if="implantat && isVisible('implantat')">
                        <img src="@/assets/implantat.svg" alt="Implantat" />
                        <span>{{ implantat }}</span>
                    </div>
                    <div v-if="avtal && isVisible('avtal')">
                        <img src="@/assets/avtal.svg" alt="Inställelsetid" />
                        <span>Avtal {{ avtal?.toLowerCase() }}</span>
                    </div>
                    <template v-if="isVisible('installelsetid')">
                        <div v-if="installelsedatum || installelsetid">
                            <img src="@/assets/installelsetid_grey.svg" alt="Inställelsetid" />
                            <span>Inställelsetid {{ formatInstallelsetid(installelsedatum, installelsetid) }}</span>
                        </div>
                        <div v-else>
                            <img src="@/assets/installelsetid_red.svg" alt="Inställelsetid" />
                            <span class="bold">{{ `Inställelsetid saknas` }}</span>
                        </div>
                    </template>
                    <div v-if="operationsinformation && isVisible('operationsinformation')">
                        <img src="@/assets/info_grey.svg" alt="Info till operation" />
                        <span>{{ operationsinformation }}</span>
                    </div>
                </section>
                <template v-if="resurskrockar?.length">
                    <section class="tool-tip-info-section">
                        <div class="resurskrockar">
                            <p v-for="resurs in resurskrockar" :key="resurs.id">
                                <img src="@/assets/warning-red.svg" />
                                <span v-if="resurs.totalt_antal < 1" class="bold">{{ `Varning inaktiv resurs:` }}</span>
                                <span v-else class="bold">{{ `Varning dubbelbokning:` }}</span>
                                <span>{{ `${resurs.namn}` }}</span>
                            </p>
                        </div>
                    </section>
                </template>
            </div>
        </div>

        <div class="style-slot-inline">
            <slot name="inline-elements"></slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";

    .calendar-tool-tip {
        padding: 24px;
        width: 523px;
        min-height: 407px;
        background-color: #ffffff;
        border: 1px solid #d3d8da;
        border-radius: 4px;

        .tool-tip-header-border-container {
            position: relative;

            .stripes-overlay {
                position: absolute;
                background: repeating-linear-gradient(-45deg, #ffffff, #ffffff 2px, transparent 2px, transparent 7px);
                z-index: 1001;
                width: 100%;
                height: 100%;
            }

            .tool-tip-header-border {
                margin-bottom: 12px;
                width: 65px;
                border-radius: 4px;
            }
        }

        .tool-tip-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-top: 10px;
            margin-bottom: 8px;

            h4 {
                font-family:
                    Roboto Medium,
                    sans-serif;
                font-size: 16px;
                line-height: 18px;
                color: $color-text-popup;
                margin-bottom: 6px;
                font-weight: 500;
            }

            p {
                font-family: Roboto, sans-serif;
                font-size: 16px;
                line-height: 18px;
                color: $color-text-popup;
                margin: 0px;
                padding: 0px;
            }
            &-right {
                color: $color-text-popup;
                line-height: 24px;
            }
        }

        .tool-tip-op-info {
            span {
                font-family: Roboto, sans-serif;
                font-size: 14px;
                color: $color-text-popup;
            }

            .tool-tip-info-wrapper {
                display: flex;
                flex-direction: column;
                line-height: 19px;

                img {
                    width: 18px;
                    height: 18px;
                    margin-right: 7px;
                }

                span {
                    margin: 0px;
                    padding: 0px;
                    margin-left: 8px;
                }

                div {
                    display: flex;
                }

                .tool-tip-info-section {
                    display: flex;
                    flex-direction: column;
                    padding-block: 12px;
                    gap: 8px;

                    &:not(:has(> *)) {
                        display: none;
                    }

                    & ~ .tool-tip-info-section {
                        border-top: 1px solid #d3d8da;
                    }
                }
            }

            .resurskrockar {
                display: flex;
                flex-direction: column;
                font-family: Roboto, sans-serif;
                font-size: 14px;
                color: $color-text-popup;
                line-height: 22px;

                > p {
                    line-height: 30px;
                    display: flex;
                    align-items: center;
                }
            }

            .bold {
                font-family:
                    Roboto Medium,
                    sans-serif;
                font-size: 14px;
            }
        }

        .style-slot-inline {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            padding-top: 22px;
            border-top: 1px solid #d3d8da;
            button {
                img {
                    position: relative;
                    right: 4px;
                    filter: invert(65%) sepia(18%) saturate(1690%) hue-rotate(154deg) brightness(87%) contrast(84%);
                }

                &:not(.button-link-without-icon) {
                    justify-content: flex-start;
                }
            }

            button:last-child {
                border: 1px solid #dba7aa;
                background-color: #dba7aa14;
            }
        }
    }
</style>
